// TrilhaCompra.js — A trilha do funil de compra: Carrinho → Dados → Pagamento.
//
// Uma trilha só para as TRÊS telas (carrinho, checkout e retorno do pagamento).
// Antes a lista de etapas estava escrita duas vezes, em FinalizarCompra e em
// CompraConcluida, e o carrinho — que é a primeira etapa — não mostrava trilha
// nenhuma: a pessoa só descobria que havia um caminho depois de já estar no
// meio dele.
//
// ================= O QUE MUDOU EM 11/09/2026 (pedido do dono) =================
//
//  1. SEM CAIXA. A trilha era um `bloco` azul com borda, e uma faixa pintada
//     no alto da página competia com o conteúdo que importa. Agora ela é
//     transparente: só as bolinhas, a linha e o texto.
//  2. TEXTO EMBAIXO DA BOLINHA, e não ao lado. Ao lado, cada etapa precisava
//     da largura de um parágrafo e as três viravam três colunas de texto; em
//     baixo, a bolinha fica no eixo da linha que liga uma etapa à outra — que
//     é o que desenha o caminho.
//  3. A LINHA LARANJA LIGA O QUE JÁ PASSOU. O trecho entre duas bolinhas fica
//     laranja quando a etapa da direita já foi alcançada, e cinza quando não.
//  4. A LINHA ANDA ANTES DA PÁGINA TROCAR. Ao seguir para a etapa seguinte, o
//     trecho se preenche da bolinha atual até a próxima e só então a navegação
//     acontece (ver `avancando` / `aoTerminar`). São 520ms: o bastante para o
//     olho acompanhar o traço, pouco o bastante para não virar espera.
//
// COMO A PÁGINA USA:
//
//   const [indo, setIndo] = useState(false);
//   <TrilhaCompra atual={0} avancando={indo} aoTerminar={() => navigate(...)} />
//   <button onClick={() => setIndo(true)}>Finalizar Pedido</button>
//
// Quem não anima (a tela de retorno, que já chegou ao fim) simplesmente não
// passa `avancando`.
import React, { useEffect, useRef, useState } from "react";

/** As etapas, na ordem. Fonte única — as três telas importam daqui. */
export const ETAPAS_COMPRA = [
  { titulo: "Carrinho", descricao: "Itens escolhidos" },
  { titulo: "Dados e revisão", descricao: "Endereço, cupom e total" },
  { titulo: "Pagamento", descricao: "PIX ou cartão na InfinitePay" },
];

/** Duração do traço, em ms. A bolinha da frente acende no fim dele. */
export const DURACAO_TRILHA = 520;

const querMenosMovimento = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

/**
 * @param {Array}  passos     lista de {titulo, descricao}
 * @param {number} atual      índice (base 0) da etapa em que a pessoa está
 * @param {bool}   avancando  true dispara o traço de `atual` para `atual + 1`
 * @param {func}   aoTerminar chamado quando o traço chega — é aqui que se navega
 */
const TrilhaCompra = ({
  passos = ETAPAS_COMPRA,
  atual = 0,
  avancando = false,
  aoTerminar,
  className = "",
}) => {
  const [correndo, setCorrendo] = useState(false);

  // O callback vive numa ref: colocá-lo nas dependências do efeito faria a
  // animação recomeçar a cada render do pai (que re-cria a função), e o
  // `aoTerminar` dispararia duas vezes — duas navegações para a mesma página.
  const fim = useRef(aoTerminar);
  fim.current = aoTerminar;

  useEffect(() => {
    if (!avancando) {
      setCorrendo(false);
      return undefined;
    }

    // Quem pediu menos movimento no sistema não fica esperando meio segundo
    // por um traço que não vai ver.
    if (querMenosMovimento()) {
      fim.current?.();
      return undefined;
    }

    // Um quadro de respiro antes de mudar o `scaleX`: sem ele o navegador
    // agrupa o estado inicial e o final no mesmo estilo computado e não há
    // transição nenhuma — a linha apareceria pronta.
    const quadro = requestAnimationFrame(() => setCorrendo(true));
    // O fim do traço é marcado por este relógio, e não pelo `transitionend`:
    // o evento não chega quando o elemento está fora da tela (a trilha rolou
    // para fora, o celular economizando bateria), e aí a navegação nunca
    // aconteceria. O relógio sempre chega; os +80ms de folga deixam o traço
    // terminar de chegar antes de a página trocar.
    const relogio = setTimeout(() => fim.current?.(), DURACAO_TRILHA + 80);
    return () => {
      cancelAnimationFrame(quadro);
      clearTimeout(relogio);
    };
  }, [avancando]);

  return (
    <ol className={"flex items-start " + className}>
      {passos.map((passo, i) => {
        const concluido = atual > i;
        const ativo = atual === i;
        // A bolinha da frente acende junto com a chegada do traço.
        const alcancado = concluido || ativo || (correndo && i === atual + 1);

        // O trecho à direita desta etapa: cheio se a próxima já foi alcançada,
        // e em movimento quando o traço está saindo justamente daqui.
        const cheio = atual > i;
        const andando = correndo && i === atual;
        const escala = cheio ? 1 : andando ? 1 : 0;

        return (
          <li
            key={passo.titulo}
            className="relative flex-1 min-w-0 flex flex-col items-center text-center px-1"
          >
            {/* ===== O trecho até a próxima bolinha =====
                As etapas têm a MESMA largura (flex-1), então meia largura para
                cada lado vai do centro desta bolinha ao centro da seguinte. O
                `1.25rem` descontado em cada ponta (o raio da bolinha `w-10`)
                faz o trecho começar e terminar na BORDA delas: a bolinha ainda
                não alcançada é oca, e um trecho que corresse até o centro
                apareceria por dentro dela, riscando o número. Não há trecho
                depois da última. */}
            {i < passos.length - 1 && (
              <span
                aria-hidden="true"
                className="absolute top-[18px] left-[calc(50%+1.25rem)] right-[calc(1.25rem-50%)] h-[3px] bg-line overflow-hidden"
              >
                <span
                  className="block h-full w-full origin-left bg-insigne-orange ease-out"
                  style={{
                    transform: `scaleX(${escala})`,
                    transitionProperty: "transform",
                    transitionDuration: andando ? `${DURACAO_TRILHA}ms` : "0ms",
                  }}
                />
              </span>
            )}

            <span
              className={
                "relative z-10 w-10 h-10 rounded-full border-2 flex items-center justify-center " +
                "text-sm font-bold transition-colors duration-200 " +
                (alcancado
                  ? "bg-insigne-orange border-insigne-orange text-paper"
                  : "bg-transparent border-line text-white/50")
              }
              // A bolinha DA FRENTE só vira laranja quando o traço termina de
              // chegar nela — acender junto com a partida faria o traço parecer
              // enfeite, e não o caminho até ali.
              style={
                correndo && i === atual + 1
                  ? { transitionDelay: `${DURACAO_TRILHA - 140}ms` }
                  : undefined
              }
            >
              {concluido ? <i className="bi bi-check-lg" /> : i + 1}
            </span>

            <p
              className={
                "mt-2.5 text-[13px] md:text-sm font-semibold leading-tight " +
                (ativo ? "text-insigne-orange" : alcancado ? "text-white" : "text-white/55")
              }
            >
              {passo.titulo}
            </p>
            {/* A linha de apoio some no celular: com três colunas de ~110px
                ela vira quatro linhas de texto por etapa e empurra a página
                inteira para baixo. O título sozinho já diz o que é. */}
            {passo.descricao && (
              <p className="hidden sm:block text-white/45 text-xs mt-1 leading-relaxed">
                {passo.descricao}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default TrilhaCompra;
